import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { AuthBootstrap } from './auth/AuthBootstrap';
import { GuestOnly } from './auth/GuestOnly';
import { RequireAuth } from './auth/RequireAuth';
import { RequireHrAccess } from './auth/RequireHrAccess';
import { RequireSecurePharmReportsAccess } from './auth/RequireSecurePharmReportsAccess';
import { RequireRole } from './auth/RequireRole';
import { RequireSettingsAccess } from './auth/RequireSettingsAccess';
import { RequireUnreleasedDepartmentAccess } from './auth/RequireUnreleasedDepartmentAccess';
import { OPS_ROLES } from './auth/roles';
import { AppShell } from './components/AppShell/AppShell';
import { EditorSessionLayout } from '@jarvis/platform-ui';
import { AccessDeniedPage } from './pages/AccessDenied/AccessDeniedPage';
import { DashboardPage } from './pages/Dashboard/DashboardPage';
import { CalendarTargetsAdminPage } from './pages/Admin/CalendarTargets/CalendarTargetsAdminPage';
import { CompanyHolidaysAdminPage } from './pages/Admin/CompanyHolidays/CompanyHolidaysAdminPage';
import { ClientPortalCustomAppsAdminPage } from './pages/Admin/ClientPortalCustomApps/ClientPortalCustomAppsAdminPage';
import { ClientPortalStorageMountsAdminPage } from './pages/Admin/ClientPortalStorageMounts/ClientPortalStorageMountsAdminPage';
import { HvgCeGroupsAdminPage } from './pages/Admin/ClientAccounts/HvgCeGroupsAdminPage';
import { HvgClientsAdminPage } from './pages/Admin/ClientAccounts/HvgClientsAdminPage';
import { ClientOffboardingNotifyContactsAdminPage } from './pages/Admin/ClientAccounts/ClientOffboardingNotifyContactsAdminPage';
import { ClientLicenseSummaryAdminPage } from './pages/Admin/ClientAccounts/ClientLicenseSummaryAdminPage';
import { DashboardTipsAdminPage } from './pages/Admin/DashboardTips/DashboardTipsAdminPage';
import { ClientPortalDashboardTipsAdminPage } from './pages/Admin/ClientPortalDashboardTips/ClientPortalDashboardTipsAdminPage';
import { ClientPortalStorageFileAuditPage } from './pages/Admin/ClientPortalStorageFileAudit/ClientPortalStorageFileAuditPage';
import { ClientSpaceWizardPage } from './pages/Admin/ClientSpaceWizard/ClientSpaceWizardPage';
import { StorageMountsAdminPage } from './pages/Admin/StorageMounts/StorageMountsAdminPage';
import { StorageFileAuditPage } from './pages/Admin/StorageFileAudit/StorageFileAuditPage';
import { UserActivityHistoryPage } from './pages/Admin/UserActivity/UserActivityHistoryPage';
import { UserActivitySummaryPage } from './pages/Admin/UserActivity/UserActivitySummaryPage';
import { ClientPortalUserActivityHistoryPage } from './pages/Admin/ClientPortalUserActivity/ClientPortalUserActivityHistoryPage';
import { ClientPortalUserActivitySummaryPage } from './pages/Admin/ClientPortalUserActivity/ClientPortalUserActivitySummaryPage';
import { ClientUserActivityOmitEmailsAdminPage } from './pages/Admin/ClientPortalUserActivity/ClientUserActivityOmitEmailsAdminPage';
import { SyncJobRunsPage } from './pages/Admin/SyncJobs/SyncJobRunsPage';
import { SyncJobsPage } from './pages/Admin/SyncJobs/SyncJobsPage';
import { TimeOffCalendarPage } from './pages/HR/TimeOff/TimeOffCalendarPage';
import { TimeOffEntriesPage } from './pages/HR/TimeOff/TimeOffEntriesPage';
import { TimeOffManagementPage } from './pages/HR/TimeOff/TimeOffManagementPage';
import { TimeOffSyncLogsPage } from './pages/HR/TimeOff/TimeOffSyncLogsPage';
import { CompanyEmployeesPage } from './pages/HR/CompanyEmployeesPage';
import { OffboardEmployeePage } from './pages/HR/Offboarding/OffboardEmployeePage';
import { OffboardingHistoryPage } from './pages/HR/Offboarding/OffboardingHistoryPage';
import { SecurePharmReportsPage } from './pages/HR/Finance/SecurePharmReportsPage';
import { LoginPage } from './pages/Login/LoginPage';
import { CEPatientTrackingPage } from './pages/Pharmacy/CEPatientTrackingPage';
import { CoveredEntityListPage } from './pages/Pharmacy/CoveredEntityListPage';
import { AlertingHistoryPage } from './pages/Pharmacy/AlertingHistoryPage';
import { PatientsPage } from './pages/Pharmacy/PatientsPage';
import { CEClientsListPage } from './pages/Sales/CEClientsListPage';
import { CeClientAccessPage } from './pages/Sales/CeClientAccessPage';
import { PeopleSyncLogsPage } from './pages/Admin/PeopleSync/PeopleSyncLogsPage';
import { FileReviewMentionsAdminPage } from './pages/Admin/FileReviewMentions/FileReviewMentionsAdminPage';
import { OurPharmaciesPage } from './pages/Admin/OurPharmaciesPage';
import { SettingsAboutPage } from './pages/Settings/SettingsAboutPage';
import { SettingsAppearancePage } from './pages/Settings/SettingsAppearancePage';
import { SettingsGeneralPage } from './pages/Settings/SettingsGeneralPage';
import { SettingsEditorsPage } from './pages/Settings/SettingsEditorsPage';
import { SettingsPage } from './pages/Settings/SettingsPage';
import { UserProfilePage } from './pages/Settings/UserProfilePage';
import {
  CustomAppLaunchPage,
  CustomAppsAdminPage,
  LegalMarkdownPage,
  MyAppsBrowsePage,
  MyAppsDocumentEditorPage,
  MyAppsDrawioEditorPage,
  MyAppsPdfViewerPage,
  MyAppsSpreadsheetEditorPage,
  OpenByStoragePage,
  WEBOPS_MY_APPS_REGISTRY
} from '@jarvis/platform-ui';
import { AdminBugsPage } from './pages/Admin/Bugs/AdminBugsPage';
import { AdminFeedbackPage } from './pages/Admin/Feedback/AdminFeedbackPage';
import { SupportMetadataAdminPage } from './pages/Admin/SupportMetadata/SupportMetadataAdminPage';
import { MyTicketsPage } from './pages/MyTickets/MyTicketsPage';
import { ClientMentionsAdminPage } from './pages/Admin/ClientMentions/ClientMentionsAdminPage';
import { TimeOffSyncLogsLegacyRedirect } from './routing/TimeOffSyncLogsLegacyRedirect';
import { ImageViewerPage } from './pages/Files/ImageViewerPage';
import { VideoViewerPage } from './pages/Files/VideoViewerPage';
import { SpreadsheetEditorPage } from './pages/Files/SpreadsheetEditorPage';
import { WordEditorPage } from './pages/Files/WordEditorPage';
import { DocumentPreviewPage } from './pages/Files/DocumentPreviewPage';
import { PdfViewerPage } from './pages/Files/PdfViewerPage';
import { TextEditorPage } from './pages/Files/TextEditorPage';
import { MarkdownEditorPage } from './pages/Files/MarkdownEditorPage';
import { DrawioEditorPage } from './pages/Files/DrawioEditorPage';
import { WorkspacePage } from './pages/Files/WorkspacePage';
import { FilesPage } from './pages/Files/FilesPage';
import { FileAccessRestrictedPage } from './pages/Files/FileAccessRestrictedPage';
import { ItComingSoonPage } from './pages/IT/ItComingSoonPage';
/** Lazy-load Code Editor so Monaco/workers/formatters are not on the main bundle. */
const CodeEditorPage = lazy(() =>
  import('./pages/Files/CodeEditorPage').then((m) => ({ default: m.CodeEditorPage }))
);

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route element={<AuthBootstrap />}>
          <Route
            path="/login"
            element={
              <GuestOnly>
                <LoginPage />
              </GuestOnly>
            }
          />
          <Route path="/access-denied" element={<AccessDeniedPage />} />
          <Route
            element={
              <RequireAuth>
                <EditorSessionLayout shell={AppShell} />
              </RequireAuth>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="files" element={<FilesPage />} />
            <Route
              path="analytics/library/*"
              element={<Navigate to="/access-denied" replace />}
            />
            <Route
              path="pharmacy/ce-tracking"
              element={<Navigate to="/pharmacy/ce-patient-tracking" replace />}
            />
            <Route
              path="pharmacy/ce-patient-tracking"
              element={
                <RequireUnreleasedDepartmentAccess area="pharmacy">
                  <CEPatientTrackingPage />
                </RequireUnreleasedDepartmentAccess>
              }
            />
            <Route
              path="pharmacy/patients"
              element={
                <RequireUnreleasedDepartmentAccess area="pharmacy">
                  <PatientsPage />
                </RequireUnreleasedDepartmentAccess>
              }
            />
            <Route
              path="pharmacy/covered-entity-list"
              element={
                <RequireUnreleasedDepartmentAccess area="pharmacy">
                  <CoveredEntityListPage />
                </RequireUnreleasedDepartmentAccess>
              }
            />
            <Route
              path="pharmacy/alerting-history"
              element={
                <RequireUnreleasedDepartmentAccess area="pharmacy">
                  <AlertingHistoryPage />
                </RequireUnreleasedDepartmentAccess>
              }
            />
            <Route
              path="hr/time-off"
              element={<Navigate to="/hr/time-off/all" replace />}
            />
            <Route
              path="hr/company-employees"
              element={
                <RequireHrAccess>
                  <CompanyEmployeesPage />
                </RequireHrAccess>
              }
            />
            <Route
              path="hr/offboarding/offboard"
              element={
                <RequireRole role="ItSysAdmin">
                  <OffboardEmployeePage />
                </RequireRole>
              }
            />
            <Route
              path="hr/offboarding/history"
              element={
                <RequireRole role="ItSysAdmin">
                  <OffboardingHistoryPage />
                </RequireRole>
              }
            />
            <Route
              path="hr/time-off/calendar"
              element={
                <RequireHrAccess>
                  <TimeOffCalendarPage />
                </RequireHrAccess>
              }
            />
            <Route
              path="hr/time-off/all"
              element={
                <RequireHrAccess>
                  <TimeOffEntriesPage />
                </RequireHrAccess>
              }
            />
            <Route
              path="hr/time-off/management"
              element={
                <RequireHrAccess>
                  <TimeOffManagementPage />
                </RequireHrAccess>
              }
            />
            <Route
              path="hr/finance/secure-pharm-reports"
              element={<Navigate to="/finance/secure-pharm-reports" replace />}
            />
            <Route
              path="finance/secure-pharm-reports"
              element={
                <RequireSecurePharmReportsAccess>
                  <SecurePharmReportsPage />
                </RequireSecurePharmReportsAccess>
              }
            />
            <Route path="hr/time-off/sync-logs" element={<TimeOffSyncLogsLegacyRedirect />} />
            <Route
              path="admin/time-off/sync-logs"
              element={
                <RequireRole role="ItSysAdmin">
                  <TimeOffSyncLogsPage />
                </RequireRole>
              }
            />
            <Route
              path="it/*"
              element={
                <RequireRole role="ItSysAdmin">
                  <ItComingSoonPage />
                </RequireRole>
              }
            />
            <Route
              path="admin/time-off/calendar-targets"
              element={
                <RequireRole role="ItSysAdmin">
                  <CalendarTargetsAdminPage />
                </RequireRole>
              }
            />
            <Route
              path="admin/time-off/company-holidays"
              element={
                <RequireRole role="ItSysAdmin">
                  <CompanyHolidaysAdminPage />
                </RequireRole>
              }
            />
            <Route
              path="admin/client-portal/storage-mounts"
              element={
                <RequireRole role="ItSysAdmin">
                  <ClientPortalStorageMountsAdminPage />
                </RequireRole>
              }
            />
            <Route
              path="admin/client-portal/storage-file-audit"
              element={
                <RequireRole role="ItSysAdmin">
                  <ClientPortalStorageFileAuditPage />
                </RequireRole>
              }
            />
            <Route
              path="admin/client-accounts/ce-groups"
              element={
                <RequireRole role="ItSysAdmin">
                  <HvgCeGroupsAdminPage />
                </RequireRole>
              }
            />
            <Route
              path="admin/client-accounts/clients"
              element={
                <RequireRole role="ItSysAdmin">
                  <HvgClientsAdminPage />
                </RequireRole>
              }
            />
            <Route
              path="admin/client-accounts/offboarding-notify"
              element={
                <RequireRole role="ItSysAdmin">
                  <ClientOffboardingNotifyContactsAdminPage />
                </RequireRole>
              }
            />
            <Route
              path="admin/client-accounts/license-summary"
              element={
                <RequireRole role="ItSysAdmin">
                  <ClientLicenseSummaryAdminPage />
                </RequireRole>
              }
            />
            <Route
              path="admin/storage-mounts"
              element={
                <RequireRole role="ItSysAdmin">
                  <StorageMountsAdminPage />
                </RequireRole>
              }
            />
            <Route
              path="admin/new-client-space"
              element={
                <RequireRole role="ItSysAdmin">
                  <ClientSpaceWizardPage />
                </RequireRole>
              }
            />
            <Route
              path="admin/storage-file-audit"
              element={
                <RequireRole role="ItSysAdmin">
                  <StorageFileAuditPage />
                </RequireRole>
              }
            />
            <Route
              path="admin/user-activity/history"
              element={
                <RequireRole role="ItSysAdmin">
                  <UserActivityHistoryPage />
                </RequireRole>
              }
            />
            <Route
              path="admin/user-activity"
              element={
                <RequireRole role="ItSysAdmin">
                  <UserActivitySummaryPage />
                </RequireRole>
              }
            />
            <Route
              path="admin/client-portal/user-activity/history"
              element={
                <RequireRole role="ItSysAdmin">
                  <ClientPortalUserActivityHistoryPage />
                </RequireRole>
              }
            />
            <Route
              path="admin/client-portal/user-activity"
              element={
                <RequireRole role="ItSysAdmin">
                  <ClientPortalUserActivitySummaryPage />
                </RequireRole>
              }
            />
            <Route
              path="admin/client-portal/tester-omit-accounts"
              element={
                <RequireRole role="ItSysAdmin">
                  <ClientUserActivityOmitEmailsAdminPage />
                </RequireRole>
              }
            />
            <Route
              path="admin/people-sync/logs"
              element={
                <RequireRole role="ItSysAdmin">
                  <PeopleSyncLogsPage />
                </RequireRole>
              }
            />
            <Route
              path="admin/system/file-review-mentions"
              element={
                <RequireRole role="ItSysAdmin">
                  <FileReviewMentionsAdminPage />
                </RequireRole>
              }
            />
            <Route
              path="admin/system/our-pharmacies"
              element={
                <RequireRole role="ItSysAdmin">
                  <OurPharmaciesPage />
                </RequireRole>
              }
            />
            <Route
              path="admin/custom-apps"
              element={
                <RequireRole role="ItSysAdmin">
                  <CustomAppsAdminPage
                    availableRoles={OPS_ROLES}
                    title="HVG Hub Custom Apps"
                  />
                </RequireRole>
              }
            />
            <Route
              path="admin/client-portal/custom-apps"
              element={
                <RequireRole role="ItSysAdmin">
                  <ClientPortalCustomAppsAdminPage />
                </RequireRole>
              }
            />
            <Route
              path="admin/sync-jobs"
              element={
                <RequireRole role="ItSysAdmin">
                  <SyncJobsPage />
                </RequireRole>
              }
            />
            <Route
              path="admin/sync-jobs/:name/runs"
              element={
                <RequireRole role="ItSysAdmin">
                  <SyncJobRunsPage />
                </RequireRole>
              }
            />
            <Route path="my-tickets" element={<MyTicketsPage />} />
            <Route
              path="admin/bugs"
              element={
                <RequireRole role="ItSysAdmin">
                  <AdminBugsPage />
                </RequireRole>
              }
            />
            <Route
              path="admin/feedback"
              element={
                <RequireRole role="ItSysAdmin">
                  <AdminFeedbackPage />
                </RequireRole>
              }
            />
            <Route
              path="admin/support-metadata"
              element={
                <RequireRole role="ItSysAdmin">
                  <SupportMetadataAdminPage />
                </RequireRole>
              }
            />
            <Route
              path="admin/dashboard-tips"
              element={
                <RequireRole role="ItSysAdmin">
                  <DashboardTipsAdminPage />
                </RequireRole>
              }
            />
            <Route
              path="admin/client-portal/dashboard-tips"
              element={
                <RequireRole role="ItSysAdmin">
                  <ClientPortalDashboardTipsAdminPage />
                </RequireRole>
              }
            />
            <Route
              path="admin/site-feedback/client-mentions"
              element={
                <RequireRole role="ItSysAdmin">
                  <ClientMentionsAdminPage />
                </RequireRole>
              }
            />
            <Route
              path="sales/ce-clients"
              element={
                <RequireUnreleasedDepartmentAccess area="sales">
                  <CEClientsListPage />
                </RequireUnreleasedDepartmentAccess>
              }
            />
            <Route
              path="sales/ce-client-access"
              element={
                <RequireUnreleasedDepartmentAccess area="sales">
                  <CeClientAccessPage />
                </RequireUnreleasedDepartmentAccess>
              }
            />
            <Route path="apps/document-editor" element={<MyAppsDocumentEditorPage />} />
            <Route path="apps/spreadsheet-editor" element={<MyAppsSpreadsheetEditorPage />} />
            <Route path="apps/pdf-viewer" element={<MyAppsPdfViewerPage />} />
            <Route path="apps/drawio-editor" element={<MyAppsDrawioEditorPage />} />
            <Route path="apps/link/:slug" element={<CustomAppLaunchPage />} />
            <Route path="files/open-by-storage" element={<OpenByStoragePage />} />
            <Route path="files/access-restricted" element={<FileAccessRestrictedPage />} />
            <Route path="files/image-viewer" element={<ImageViewerPage />} />
            <Route path="files/video-viewer" element={<VideoViewerPage />} />
            <Route path="files/spreadsheet-editor" element={<SpreadsheetEditorPage />} />
            <Route path="files/document-editor" element={<WordEditorPage />} />
            <Route path="files/document-preview" element={<DocumentPreviewPage />} />
            <Route path="files/pdf-viewer" element={<PdfViewerPage />} />
            <Route path="files/text-editor" element={<TextEditorPage />} />
            <Route
              path="files/code-editor"
              element={
                <Suspense fallback={<div style={{ padding: '1.5rem' }}>Loading Code Editor…</div>}>
                  <CodeEditorPage />
                </Suspense>
              }
            />
            <Route path="files/markdown-editor" element={<MarkdownEditorPage />} />
            <Route path="files/drawio-editor" element={<DrawioEditorPage />} />
            <Route path="files/workspace" element={<WorkspacePage />} />
            <Route
              path="settings/profile"
              element={
                <RequireAuth>
                  <UserProfilePage />
                </RequireAuth>
              }
            />
            <Route
              path="settings/my-apps"
              element={
                <RequireAuth>
                  <MyAppsBrowsePage
                    registry={WEBOPS_MY_APPS_REGISTRY}
                    subtitle="Browse the apps available in the Operations Hub, including custom links IT added. Pin shortcuts for the sidebar, dashboard, and Ctrl+K launcher."
                  />
                </RequireAuth>
              }
            />
            <Route
              path="settings"
              element={
                <RequireSettingsAccess>
                  <SettingsPage />
                </RequireSettingsAccess>
              }
            >
              <Route path="general" element={<SettingsGeneralPage />} />
              <Route path="appearance" element={<SettingsAppearancePage />} />
              <Route path="editors" element={<SettingsEditorsPage />} />
              <Route path="about" element={<SettingsAboutPage />} />
            </Route>
            <Route
              path="settings/privacy"
              element={
                <RequireSettingsAccess>
                  <LegalMarkdownPage markdownPath="/legal/privacy.md" title="Privacy Policy" />
                </RequireSettingsAccess>
              }
            />
            <Route
              path="settings/cookies"
              element={
                <RequireSettingsAccess>
                  <LegalMarkdownPage markdownPath="/legal/cookie-policy.md" title="Cookie Policy" />
                </RequireSettingsAccess>
              }
            />
            <Route
              path="settings/terms"
              element={
                <RequireSettingsAccess>
                  <LegalMarkdownPage markdownPath="/legal/terms-of-use.md" title="Terms of Use" />
                </RequireSettingsAccess>
              }
            />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
